import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip, TextField, InputAdornment, FormControl, Select, MenuItem, Checkbox, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import RTGSActionDialog from './RTGSActionDialog';
import AmendTransactionDialog from './AmendTransactionDialog';
import CancelTransactionDialog from './CancelTransactionDialog';
import { useToast } from './ui/ToastProvider';
import { VALID_MTOS, VALID_SUB_AGENTS } from '../utils/constants';
import { transactionStore } from '../utils/transactionStore';
import { DEMO_TRANSACTIONS } from '../utils/demoTransactions';
type PayoutType = 'FT' | 'IBFT' | 'OTC';
type LifecycleStatus = 'FAILED';
type CurrentState = 'TITLE_FETCH' | 'BALANCE_CHECK' | 'FT_POST' | 'RAAST_POST' | 'ONELINK_POST' | 'RTGS_POST';
interface FailedTransaction {
  id: string;
  mtoName: string;
  mtoRef: string;
  bankRef: string;
  branchCode: string;
  agentCode: string;
  payoutType: PayoutType;
  amountFCY: string;
  currencyFCY: string;
  amountPKR: string;
  corridor: string;
  purposeCode: string;
  purposeText: string;
  remitterName: string;
  beneficiaryName: string;
  beneficiaryIBAN: string;
  currentState: CurrentState;
  lifecycleStatus: LifecycleStatus;
  createdAt: string;
  updatedAt: string;
  failedAt: string;
  attemptCount: number;
  canMoveToRTGS: boolean;
  railRefs: {
    RRN?: string;
    STAN?: string;
    RaastMsgId?: string;
    UETR?: string;
  };
  stateHistory: any[];
}
// Mock failed transactions
export const MOCK_FAILED_TRANSACTIONS: FailedTransaction[] = Array(30).fill(null).map((_, index) => {
  const payoutTypes: PayoutType[] = ['FT', 'IBFT', 'OTC'];
  const payoutType = payoutTypes[index % 3];
  const failStates: CurrentState[] = payoutType === 'FT' ? ['TITLE_FETCH', 'BALANCE_CHECK', 'FT_POST'] : ['TITLE_FETCH', 'RAAST_POST', 'ONELINK_POST'];
  const currentState = failStates[index % failStates.length];
  const canMoveToRTGS = ['TITLE_FETCH', 'BALANCE_CHECK', 'FT_POST'].includes(currentState);
  return {
    id: `TXN-${50000 + index}`,
    mtoName: index % 10 === 0 ? '' : VALID_MTOS[index % VALID_MTOS.length],
    mtoRef: `MTO-${60000 + index}`,
    bankRef: `BNK-${70000 + index}`,
    branchCode: payoutType === 'OTC' ? ['GH-ACC-001', 'NG-LAG-001', 'SN-DKR-001'][index % 3] : '',
    agentCode: payoutType === 'OTC' ? VALID_SUB_AGENTS[index % VALID_SUB_AGENTS.length] : '',
    payoutType,
    amountFCY: (1000 + index * 50).toFixed(2),
    currencyFCY: 'USD',
    amountPKR: ((1000 + index * 50) * 15).toLocaleString(),
    corridor: ['NG-US', 'KE-DE', 'SN-FR', 'GH-UK'][index % 4],
    purposeCode: ['FAM', 'SAL', 'EDU', 'MED'][index % 4],
    purposeText: ['Personal Remittance', 'Business Payment', 'Education', 'Medical'][index % 4],
    remitterName: `Remitter ${index + 1}`,
    beneficiaryName: `Beneficiary ${index + 1}`,
    beneficiaryIBAN: `GB29NWBK601613319268${String(index).padStart(2, '0')}`,
    currentState,
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - (index + 1) * 3600000).toISOString(),
    updatedAt: new Date(Date.now() - index * 3600000).toISOString(),
    failedAt: new Date(Date.now() - index * 3600000).toISOString(),
    attemptCount: Math.floor(index / 10) + 1,
    canMoveToRTGS,
    railRefs: {},
    stateHistory: [{
      stateCode: currentState,
      status: 'FAIL',
      timestamp: new Date(Date.now() - index * 3600000).toISOString(),
      primaryRef: `${currentState}-${index}`,
      attempts: Math.floor(index / 10) + 1
    }]
  };
});
const FailedTransactionsTable: React.FC = () => {
  const [transactions, setTransactions] = useState<FailedTransaction[]>(MOCK_FAILED_TRANSACTIONS);
  const [filteredTransactions, setFilteredTransactions] = useState<FailedTransaction[]>(MOCK_FAILED_TRANSACTIONS);
  const [selectedTransaction, setSelectedTransaction] = useState<FailedTransaction | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rtgsDialogOpen, setRtgsDialogOpen] = useState(false);
  const [filterChips, setFilterChips] = useState<FilterChip[]>([]);
  const [filters, setFilters] = useState({
    search: '',
    payoutType: [] as PayoutType[],
    currentState: [] as CurrentState[],
    mtoRef: '',
    txnId: ''
  });
  const {
    showToast
  } = useToast();
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAmendTransaction, setSelectedAmendTransaction] = useState<FailedTransaction | null>(null);
  const [selectedCancelTransaction, setSelectedCancelTransaction] = useState<FailedTransaction | null>(null);
  const [storeUpdateCount, setStoreUpdateCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkCancelDialogOpen, setBulkCancelDialogOpen] = useState(false);
  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [selectedSettleTransaction, setSelectedSettleTransaction] = useState<FailedTransaction | null>(null);
  const [settleReason, setSettleReason] = useState('');
  // Add demo failed transactions
  const demoFailedTransactions = DEMO_TRANSACTIONS.filter((t: any) => t.lifecycleStatus === 'FAILED' || t.lifecycleStatus === 'IN_PROGRESS') as any[];
  const allFailedTransactions = [...MOCK_FAILED_TRANSACTIONS, ...demoFailedTransactions];
  // Subscribe to store changes to remove approved amendments from failed list
  useEffect(() => {
    const handleStoreUpdate = () => {
      setStoreUpdateCount(prev => prev + 1);
    };
    const unsubscribe = transactionStore.subscribe(handleStoreUpdate);
    return unsubscribe;
  }, []);
  // Initialize and update transactions based on store overrides
  useEffect(() => {
    // Apply store overrides to filter out approved amendments (they move to IN_PROGRESS)
    const updatedTransactions = allFailedTransactions.map((txn: any) => {
      const override = transactionStore.getTransactionOverride(txn.id);
      if (override) {
        return {
          ...txn,
          amendStatus: override.amendStatus,
          ...(override.lifecycleStatus && {
            lifecycleStatus: override.lifecycleStatus
          }),
          ...(override.beneficiaryIBAN && {
            beneficiaryIBAN: override.beneficiaryIBAN
          }),
          ...(override.beneficiaryName && {
            beneficiaryName: override.beneficiaryName
          }),
          ...(override.updatedAt && {
            updatedAt: override.updatedAt
          })
        };
      }
      // Check for pending amendment status
      const amendStatus = transactionStore.getAmendmentStatusForTransaction(txn.id);
      if (amendStatus !== 'NONE') {
        return {
          ...txn,
          amendStatus
        };
      }
      return txn;
    })
    // Filter out transactions that have been moved to RTGS
    .filter((txn: any) => {
      const rtgsOverride = transactionStore.getRTGSOverride(txn.id);
      if (rtgsOverride) {
        return false;
      }
      return true;
    })
    // Filter out transactions whose amendments were approved (they're no longer FAILED)
    .filter((txn: any) => {
      const override = transactionStore.getTransactionOverride(txn.id);
      if (override && override.amendStatus === 'APPROVED' && override.lifecycleStatus === 'IN_PROGRESS') {
        return false;
      }
      return true;
    })
    // Filter out repushed transactions
    .filter((txn: any) => {
      const repushed = transactionStore.getRepushedTransactions();
      return !repushed.some(r => r.id === txn.id);
    })
    // Filter out transactions submitted for manual settlement (PENDING_CHECKER or APPROVED)
    .filter((txn: any) => {
      const settlement = transactionStore.getManualSettlementForTransaction(txn.id);
      if (settlement && (settlement.status === 'PENDING_CHECKER' || settlement.status === 'APPROVED')) {
        return false;
      }
      return true;
    });
    setTransactions(updatedTransactions);
  }, [storeUpdateCount]);
  useEffect(() => {
    let filtered = transactions;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(txn => txn.id.toLowerCase().includes(searchLower) || txn.mtoName.toLowerCase().includes(searchLower) || txn.mtoRef.toLowerCase().includes(searchLower) || txn.bankRef.toLowerCase().includes(searchLower));
    }
    if (filters.txnId) {
      filtered = filtered.filter(txn => txn.id.toLowerCase().includes(filters.txnId.toLowerCase()));
    }
    if (filters.payoutType.length > 0) {
      filtered = filtered.filter(txn => filters.payoutType.includes(txn.payoutType));
    }
    if (filters.currentState.length > 0) {
      filtered = filtered.filter(txn => filters.currentState.includes(txn.currentState));
    }
    setFilteredTransactions(filtered);
  }, [filters, transactions]);
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(new Set(filteredTransactions.map(t => t.id)));
    } else {
      setSelectedIds(new Set());
    }
  };
  const handleSelectOne = (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    const newSelected = new Set(selectedIds);
    if (event.target.checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };
  const handleBulkCancelClick = () => {
    if (selectedIds.size === 0) return;
    setBulkCancelDialogOpen(true);
  };
  const handleBulkCancelConfirm = async (reason: string) => {
    const count = selectedIds.size;
    setTransactions(prev => prev.filter(t => !selectedIds.has(t.id)));
    setSelectedIds(new Set());
    showToast(`Cancellation request submitted for ${count} transactions`, 'success');
  };
  const handleAmendClick = (transaction: FailedTransaction) => {
    setSelectedAmendTransaction(transaction);
    setAmendDialogOpen(true);
  };
  const handleAmendConfirm = async (reason: string, newAccount?: string, fetchedTitle?: string) => {
    if (!selectedAmendTransaction) return;
    // Submit amendment to the shared transaction store
    transactionStore.submitAmendment(selectedAmendTransaction, newAccount || '', fetchedTitle || '', reason);
    // Update the transaction's amendStatus in the local list
    setTransactions(prev => prev.map(t => t.id === selectedAmendTransaction.id ? {
      ...t,
      amendStatus: 'PENDING'
    } as any : t));
    showToast(`Amendment request submitted for ${selectedAmendTransaction.id}. Sent to checker for approval.`, 'success');
    setAmendDialogOpen(false);
    setSelectedAmendTransaction(null);
  };
  const handleCancelClick = (transaction: FailedTransaction) => {
    setSelectedCancelTransaction(transaction);
    setCancelDialogOpen(true);
  };
  const handleCancelConfirm = async (reason: string) => {
    if (!selectedCancelTransaction) return;
    showToast(`Cancellation request submitted for ${selectedCancelTransaction.id}`, 'success');
    setCancelDialogOpen(false);
    setSelectedCancelTransaction(null);
  };
  const handleRepush = (transaction: FailedTransaction) => {
    // Add to repushed transactions store
    transactionStore.repushTransaction(transaction);
    // Remove from failed transactions list
    setTransactions(prev => prev.filter(t => t.id !== transaction.id));
    showToast(`Transaction ${transaction.id} repushed and will resume from ${transaction.currentState.replace(/_/g, ' ')}`, 'success');
  };
  const handleMoveToRTGS = (transaction: FailedTransaction) => {
    setSelectedTransaction(transaction);
    setRtgsDialogOpen(true);
  };
  const handleRTGSConfirm = (notes?: string) => {
    if (!selectedTransaction) return;
    // Add to RTGS store - pass notes so they're tracked
    transactionStore.addToRTGS(selectedTransaction, notes);
    // Remove from failed transactions list
    setTransactions(prev => prev.filter(t => t.id !== selectedTransaction.id));
    showToast(`Transaction ${selectedTransaction.id} moved to RTGS`, 'success');
    setRtgsDialogOpen(false);
    setSelectedTransaction(null);
  };
  const handleManualSettleClick = (transaction: FailedTransaction) => {
    setSelectedSettleTransaction(transaction);
    setSettleReason('');
    setSettleDialogOpen(true);
  };
  const handleManualSettleConfirm = () => {
    if (!selectedSettleTransaction || !settleReason.trim()) return;
    transactionStore.submitManualSettlement(selectedSettleTransaction, settleReason.trim());
    setTransactions(prev => prev.filter(t => t.id !== selectedSettleTransaction.id));
    showToast(`Manual settlement submitted for ${selectedSettleTransaction.id}. Sent to checker for approval.`, 'success');
    setSettleDialogOpen(false);
    setSelectedSettleTransaction(null);
    setSettleReason('');
  };
  const rowActions: RowAction<FailedTransaction>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => {
      setSelectedTransaction(row);
      setDrawerOpen(true);
    }
  }, {
    icon: 'lucide:edit',
    label: 'Amend',
    onClick: row => handleAmendClick(row),
    condition: (row: any) => (row.payoutType === 'FT' || row.payoutType === 'IBFT') && row.currentState === 'TITLE_FETCH' && row.lifecycleStatus === 'FAILED' && (!row.amendStatus || row.amendStatus === 'NONE' || row.amendStatus === 'REJECTED')
  }, {
    icon: 'lucide:x-circle',
    label: 'Cancel',
    onClick: row => handleCancelClick(row)
  }, {
    icon: 'lucide:rotate-cw',
    label: 'Repush',
    onClick: row => handleRepush(row)
  }, {
    icon: 'lucide:arrow-right-circle',
    label: 'Move to RTGS',
    onClick: row => handleMoveToRTGS(row),
    condition: row => row.canMoveToRTGS
  }, {
    icon: 'lucide:stamp',
    label: 'Manually Settle',
    onClick: row => handleManualSettleClick(row),
    condition: (row: any) => {
      const hasDuplicateTxn = row.stateHistory?.some((entry: any) => entry.failReasonCode === 'DUPLICATE_TXN');
      if (!hasDuplicateTxn) return false;
      const settlement = transactionStore.getManualSettlementForTransaction(row.id);
      if (settlement && (settlement.status === 'PENDING_CHECKER' || settlement.status === 'APPROVED')) {
        return false;
      }
      return true;
    }
  }];
  const columns: Column<FailedTransaction>[] = [{
    id: 'selection',
    label: '',
    renderCell: row => <Checkbox checked={selectedIds.has(row.id)} onChange={e => handleSelectOne(e, row.id)} size="small" onClick={e => e.stopPropagation()} data-id="element-1011" />
  }, {
    id: 'id',
    label: 'Txn ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1012">
          {row.id}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.txnId} onChange={e => setFilters({
      ...filters,
      txnId: e.target.value
    })} sx={{
      width: '100%',
      minWidth: 120
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1014">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1015" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1013" />
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Box sx={{
      minWidth: 150
    }} data-id="element-1016">
          {row.mtoName ? <Typography variant="body2" fontWeight={500} noWrap data-id="element-1017">
              {row.mtoName}
            </Typography> : <Chip label="No MTO" size="small" sx={{
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        color: '#EF4444',
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-1018" />}
        </Box>,
    renderFilter: () => <FormControl size="small" fullWidth sx={{
      minWidth: 120
    }} data-id="element-1019">
          <Select value={filters.mtoRef || 'all'} onChange={e => setFilters({
        ...filters,
        mtoRef: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-1020">
            <MenuItem value="all" data-id="element-1021">All</MenuItem>
            {VALID_MTOS.map(name => <MenuItem key={name} value={name} data-id="element-1022">
                {name}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: row.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(139, 92, 246, 0.1)',
      color: row.payoutType === 'FT' ? '#0EA5E9' : '#8B5CF6',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1023" />,
    renderFilter: () => <FormControl size="small" fullWidth sx={{
      minWidth: 100
    }} data-id="element-1024">
          <Select multiple value={filters.payoutType} onChange={e => setFilters({
        ...filters,
        payoutType: e.target.value as PayoutType[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.join(', ')} data-id="element-1025">
            <MenuItem value="FT" data-id="element-1026">FT</MenuItem>
            <MenuItem value="IBFT" data-id="element-1027">IBFT</MenuItem>
            <MenuItem value="OTC" data-id="element-1028">OTC</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      minWidth: 100
    }} data-id="element-1029">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'currentState',
    label: 'Failed Stage',
    renderCell: row => <Chip label={row.currentState.replace(/_/g, ' ')} size="small" sx={{
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444',
      fontWeight: 600,
      fontSize: '0.7rem',
      minWidth: 120
    }} data-id="element-1030" />,
    renderFilter: () => <FormControl size="small" fullWidth sx={{
      minWidth: 120
    }} data-id="element-1031">
          <Select multiple value={filters.currentState} onChange={e => setFilters({
        ...filters,
        currentState: e.target.value as CurrentState[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.map(s => s.replace(/_/g, ' ')).join(', ')} data-id="element-1032">
            <MenuItem value="TITLE_FETCH" data-id="element-1033">TITLE FETCH</MenuItem>
            <MenuItem value="BALANCE_CHECK" data-id="element-1034">BALANCE CHECK</MenuItem>
            <MenuItem value="FT_POST" data-id="element-1035">FT POST</MenuItem>
            <MenuItem value="RAAST_POST" data-id="element-1036">RAAST POST</MenuItem>
            <MenuItem value="ONELINK_POST" data-id="element-1037">ONELINK POST</MenuItem>
            <MenuItem value="RTGS_POST" data-id="element-1038">RTGS POST</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'attemptCount',
    label: 'Attempts',
    renderCell: row => <Chip label={`${row.attemptCount} attempt${row.attemptCount > 1 ? 's' : ''}`} size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1039" />
  }, {
    id: 'lifecycleStatus',
    label: 'Lifecycle',
    renderCell: row => <Chip label="FAILED" size="small" sx={{
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1040" />
  }, {
    id: 'failedAt',
    label: 'Failed At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1041">
          {new Date(row.failedAt).toLocaleString()}
        </Typography>
  }];
  const isAllSelected = filteredTransactions.length > 0 && selectedIds.size === filteredTransactions.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < filteredTransactions.length;
  return <>
      <Box sx={{
      mb: 2,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 2
    }} data-id="element-1042">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }} data-id="element-1043">
          <Checkbox checked={isAllSelected} indeterminate={isIndeterminate} onChange={handleSelectAll} disabled={filteredTransactions.length === 0} data-id="element-1044" />
          <Typography variant="body2" fontWeight={500} data-id="element-1045">
            {selectedIds.size} selected
          </Typography>
        </Box>
        <Button variant="contained" color="error" disabled={selectedIds.size === 0} startIcon={<Icon icon="lucide:x-circle" data-id="element-1047" />} onClick={handleBulkCancelClick} sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '8px',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
        }
      }} data-id="element-1046">
          Cancel Selected ({selectedIds.size})
        </Button>
      </Box>

      <DataTable title="Failed Transactions" data={filteredTransactions} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={() => setFilterChips([])} emptyStateMessage="No failed transactions found" initialVisibleColumns={['selection', 'id', 'mtoName', 'payoutType', 'amountFCY', 'currentState', 'attemptCount', 'lifecycleStatus', 'failedAt']} onRowClick={row => {
      setSelectedTransaction(row);
      setDrawerOpen(true);
    }} data-id="element-1048" />

      {selectedTransaction && <TransactionDetailsDrawer open={drawerOpen} onClose={() => {
      setDrawerOpen(false);
      setSelectedTransaction(null);
    }} transaction={{
      ...selectedTransaction,
      currentState: selectedTransaction.currentState as any,
      lifecycleStatus: 'FAILED' as any
    } as any} data-id="element-1049" />}

      {selectedAmendTransaction && <AmendTransactionDialog open={amendDialogOpen} onClose={() => {
      setAmendDialogOpen(false);
      setSelectedAmendTransaction(null);
    }} onConfirm={handleAmendConfirm} transaction={selectedAmendTransaction} data-id="element-1050" />}

      {selectedCancelTransaction && <CancelTransactionDialog open={cancelDialogOpen} onClose={() => {
      setCancelDialogOpen(false);
      setSelectedCancelTransaction(null);
    }} onConfirm={handleCancelConfirm} transactionId={selectedCancelTransaction.id} cancellationAction={(selectedCancelTransaction as any).cancellationAction || 'SIMPLE_CANCEL'} data-id="element-1051" />}

      <CancelTransactionDialog open={bulkCancelDialogOpen} onClose={() => setBulkCancelDialogOpen(false)} onConfirm={handleBulkCancelConfirm} transactionId={`${selectedIds.size} selected transactions`} cancellationAction="BULK_CANCEL" data-id="element-1052" />

      {selectedTransaction && <RTGSActionDialog open={rtgsDialogOpen} onClose={() => {
      setRtgsDialogOpen(false);
      setSelectedTransaction(null);
    }} onConfirm={handleRTGSConfirm} transaction={selectedTransaction} action="send" data-id="element-1053" />}

      {/* Manual Settlement Dialog */}
      <Dialog open={settleDialogOpen} onClose={() => {
      setSettleDialogOpen(false);
      setSelectedSettleTransaction(null);
      setSettleReason('');
    }} maxWidth="sm" fullWidth data-id="element-1054">
        <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-1055">
          <Icon icon="lucide:stamp" width={24} height={24} color="#0EA5E9" data-id="element-1056" />
          <Typography variant="h6" fontWeight={700} data-id="element-1057">
            Manually Settle Transaction
          </Typography>
        </DialogTitle>
        <DialogContent data-id="element-1058">
          <Alert severity="info" sx={{
          mb: 2,
          mt: 1
        }} data-id="element-1059">
            This transaction failed with a <strong data-id="element-1060">DUPLICATE_TXN</strong>{' '}
            response from 1Link, indicating the beneficiary may have already
            been credited. Manual settlement will mark this transaction as
            settled after checker approval.
          </Alert>
          {selectedSettleTransaction && <Box sx={{
          mb: 2,
          p: 1.5,
          bgcolor: 'rgba(0,0,0,0.02)',
          borderRadius: 1
        }} data-id="element-1061">
              <Typography variant="body2" fontWeight={600} data-id="element-1062">
                Transaction: {selectedSettleTransaction.id}
              </Typography>
              <Typography variant="body2" color="text.secondary" data-id="element-1063">
                Beneficiary: {selectedSettleTransaction.beneficiaryName}
              </Typography>
              <Typography variant="body2" color="text.secondary" data-id="element-1064">
                Amount: {selectedSettleTransaction.currencyFCY}{' '}
                {selectedSettleTransaction.amountFCY}
              </Typography>
            </Box>}
          <TextField label="Settlement Reason" placeholder="Provide reason for manual settlement (e.g., confirmed credit via 1Link inquiry)" multiline rows={3} fullWidth required value={settleReason} onChange={e => setSettleReason(e.target.value)} error={settleDialogOpen && settleReason.trim() === ''} helperText={settleDialogOpen && settleReason.trim() === '' ? 'Reason is required' : ''} data-id="element-1065" />
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-1066">
          <Button onClick={() => {
          setSettleDialogOpen(false);
          setSelectedSettleTransaction(null);
          setSettleReason('');
        }} sx={{
          textTransform: 'none'
        }} data-id="element-1067">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleManualSettleConfirm} disabled={!settleReason.trim()} startIcon={<Icon icon="lucide:stamp" data-id="element-1069" />} sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '8px',
          boxShadow: 'none',
          bgcolor: '#0EA5E9',
          '&:hover': {
            bgcolor: '#0284C7'
          }
        }} data-id="element-1068">
            Submit for Settlement
          </Button>
        </DialogActions>
      </Dialog>
    </>;
};
export default FailedTransactionsTable;